import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import "./EventPayment.scss";
import { apiService } from "../../services";
import { Constants } from "@eagerdog/constants";
import { toast } from "../../components/Toast/ToastManager";
import { AxiosError } from "axios/index";
import { Helmet } from "react-helmet";
import moment from "moment/moment";
import {
  EventDogShowsDto,
  EventRegistrationCreateDto,
  IAddress,
  IDog, IDogClass, IShow,
  ITransactionPaymentSummary,
  ITransactionShowDetails,
  IUser
} from "@eagerdog/interfaces";
import { helperService } from "../../services/helper.service";
import UserAddress from "../EventRegister/UserAddress/UserAddress";
import Cart from "../../components/Cart/Cart";
import { loadStripe, Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Config } from "@eagerdog/config";
import Checkout from "../EventRegister/Checkout/Checkout";
import { Elements } from "@stripe/react-stripe-js";
import { IAddonUI } from "../EventRegister/EventRegister";
import Button from "../../components/Button/Button";
import Input from "../../components/Input/Input";

interface IProps {}

export interface IWaitlistedDogInfo extends IDog {
  start_minutes?: number,
  time_slot?: string,
  classes?: IDogClass[],
  plan_to_earn_title?: boolean,
  title?: string,
  using_agent?: boolean,
  agent_name?: string,
  agent_number?: string,
  using_non_agent?: boolean,
  agent_phone?: string,
  agent_email?: string,

  substitute_name?: string,
  substitute_email?: string,
  substitute_phone?: string,

  leg_info?: string,

  splashes?: number,
  dog_class?: IDogClass
}

export interface IWaitlistedShowDogs extends IShow {
  dogs: IWaitlistedDogInfo[],
  created_by?: string,
  club_id: string
}

const EventPayment: React.FC<IProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  let { eid } = useParams();

  const user:IUser = helperService.getUser();

  const [event, setEvent] = useState<any>();
  const [userAddress, setUserAddress] = useState<IAddress>();
  const [userPhone, setUserPhone] = useState<string>();
  const [loadedShows, setLoadedShows] = useState<boolean>(false);
  const [loadedAddons, setLoadedAddons] = useState<boolean>(false);
  const [shows, setShows] = useState<IWaitlistedShowDogs[]>([]);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
  const [stripeOptions, setStripeOptions] = useState<StripeElementsOptions>();
  const [currentTransactionId, setTransactionId] = useState<string | undefined>(undefined);
  const [currentPaymentIntentId, setPaymentIntentId] = useState<string | undefined>(undefined);
  const [addons, setAddons] = useState<IAddonUI[]>([]);
  const [currentDiscountCode, setCurrentDiscountCode] = useState<string | undefined>(undefined);
  const [hasAvailableSpots, setHasAvailableSpots] = useState<boolean | null>(null); // null = not yet determined

  const defaultPaymentSummary:ITransactionPaymentSummary = useMemo(() => {
    return {
      account_number: "",
      base_amount: 0,
      discount: 0,
      currency: "USD",
      calculated_application_fee: 0,
      calculated_processing_fee: 0,
      calculated_processing_tax: 0,
      fees: 0,
      total_amount: 0,
      details: []
    };
  }, []);
  const [paymentSummary, setPaymentSummary] = useState<ITransactionPaymentSummary>(defaultPaymentSummary);

  if (!helperService.isLoggedIn()) {
    navigate(`/login?r=${location.pathname}`);
  }

  useEffect(() => {
    if (eid) {
      apiService.getEventSummaryForHandle(eid).then((response) => {
        setEvent(response);
        if (response._id) {
          apiService.getWaitlistedShows(response._id).then((eventResponse) => {
            setShows(eventResponse)
            setLoadedShows(true)
          });
          apiService.getEventAddOns(response._id).then((addOnsResponse) => {
            setAddons(addOnsResponse.map((a: any) => { return { ...a, amount: 0 }; }));
            setLoadedAddons(true)
          });
        }
      }).catch((e: AxiosError) => {
        toast.show({
          title: "Get Event",
          content: "Unable to get shows",
          duration: 10000,
          errorDetails: e,
          type: "fail",
        });
      })

    }
  }, [eid]);

  const updatePaymentSummary = useCallback(() => {
    if (shows.length > 0) {
      let _payload:EventDogShowsDto[] = [];

      for (let showId in shows) {
        if (shows[showId].dogs.length > 0) {
          let _showDogs:any = {
            show_id: shows[showId]._id,
            dogs: []
          }

          const dogMap: Record<string, any> = {};

          for (let dog of shows[showId].dogs) {
            const dogId = dog._id;

            if (!dogMap[dogId]) {
              dogMap[dogId] = {
                dog_id: dogId,
                dog_classes: []
              };
            }

            dogMap[dogId].dog_classes.push(dog.dog_class);
          }

          _showDogs.dogs = Object.values(dogMap);
          _payload.push(_showDogs);
        }
      }

      // No awaiting_payment waitlist entries for this user — nothing to pay yet
      if (_payload.length === 0) {
        setHasAvailableSpots(false);
        return;
      }

      setHasAvailableSpots(true);

      let payload:EventRegistrationCreateDto = {
        registration_kind: Constants.registration_kind.owner,
        payment_method_type: "card",
        owner_id: user._id,
        "dog_show_registrations": _payload,
        need_check_waitlists: false
      }

      if (currentDiscountCode) {
        payload.discount_code = currentDiscountCode;
      }

      if (currentTransactionId) {
        payload.transaction_id = currentTransactionId;
      }

      if (currentPaymentIntentId) {
        payload.payment_intent_id = currentPaymentIntentId;
      }

      if (addons) {
        let hasAddons = addons.filter((a: any) => a.amount > 0);

        if (hasAddons.length > 0) {
          payload.add_ons = hasAddons.map((a: IAddonUI) => {
            return {
              add_on_id: a._id,
              quantity: a.amount
            };
          });
        }
      }

      apiService.getRegistrationPaymentSummary(event._id, payload).then((response) => {
        setPaymentSummary(response);
        setStripePromise(loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY || "", { stripeAccount: response.account_number }));

        if (currentDiscountCode && response.discount > 0) {
          toast.show({
            title: "Discount Applied",
            content: "A discount has been applied",
            duration: 10000,
            type: "success"
          });
        } else if (currentDiscountCode) {
          toast.show({
            title: "Discount Invalid",
            content: "The discount code you entered is invalid",
            duration: 10000,
            type: "fail"
          });
        }
      }).catch((e: any) => {
        toast.show({
          title: "Event Register",
          content: e.response?.data?.message ? e.response?.data?.message : "Something went wrong while getting the payment summary",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      });

      apiService.getEventWaitlistedPaymentIntent(event._id, payload).then((response) => {
        if (!payload.payment_intent_id) {
          if (response?.client_secret) {
            setStripeOptions({
              clientSecret: response.client_secret
            });
          }
          if (response?.transaction_id) {
            setTransactionId(response.transaction_id);
          }
          if (response?.payment_intent_id) {
            setPaymentIntentId(response.payment_intent_id);
          }
        }
      }).catch((e: any) => {
        toast.show({
          title: "Event Register",
          content: e.response?.data?.message ? e.response?.data?.message : "Something went wrong while getting the payment summary",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      });
    }
  }, [event, shows, user._id, addons, currentTransactionId, currentPaymentIntentId])

  useEffect(() => {
    if (loadedAddons && loadedShows) {
      updatePaymentSummary()
    }
  }, [shows, addons, currentDiscountCode, currentTransactionId, loadedAddons, loadedShows])

  const getDogIds = () => {
    let dogIds:string[] = [];

    for (let s in shows) {
      for (let d in shows[s].dogs) {
        if (!dogIds.includes(shows[s].dogs[d]._id)) {
          dogIds.push(shows[s].dogs[d]._id);
        }
      }
    }

    return dogIds.join();
  }

  const checkIfWaitlist = () => {
    if (paymentSummary) {
      for (let d in paymentSummary.details) {
        for (let dog in (paymentSummary.details[d] as ITransactionShowDetails).dogs) {
          let _dog:any = (paymentSummary.details[d] as ITransactionShowDetails).dogs[dog];
          for (let _se in _dog.show_elements) {
            if (_dog.show_elements[_se].waitlist && _dog.show_elements[_se].waitlist === true) {
              return "true";
            }
          }
        }
      }
    }

    return "false";
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: paymentSummary.currency.toUpperCase() || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return (
    <div className="EventLottery">
      {event && <Helmet>
        <title>{event?.title} Event {event.is_lottery ? "- Random Draw": ""}</title>
        <meta name="description" content={`${event.is_lottery ? "Random Draw for": ""}` + event?.title + " event on " + moment(event?.start_date).format('dddd MMMM Do, yyyy') + "."} />
        <meta name="robots" content="index, follow" />
      </Helmet>}
      <div className="registerWrap payNow">
        <div className="registerInner">
          <div className="breadcrumb">
            {event?.title &&
              <div className="path">
                {event.is_lottery && <><Link to={"/e/" + eid}>{event?.title}</Link>/</>}
                {!event.is_lottery && <div className="current">{event?.title}/</div>}
              </div>
            }
            <div className="current">Payment</div>
          </div>
          {hasAvailableSpots === false && (
            <div className="paymentWrap">
              <div className="noSpotsMessage">
                <p><strong>No waitlist spots are currently available for payment.</strong></p>
                <p>Your entries are still on the waitlist. You'll receive a notification when a spot opens up and payment is required.</p>
              </div>
            </div>
          )}
          {hasAvailableSpots !== false && <div className="paymentWrap">
            <div className="title">Owner Information</div>
            <UserAddress
              onBehalf={false}
              user={user}
              onChange={(address: IAddress, phone: string) => {
                setUserAddress(address);
                setUserPhone(phone);
              }}
            />
            {addons && addons.length > 0 && (
              <div className="addons">
                <div className="title">Addons</div>
                {addons.map((addon: IAddonUI, i: number) => {
                  const hasAvailabilityLimit =
                    addon.available_quantity !== undefined &&
                    addon.available_quantity !== null;
                  const remainingQuantity = hasAvailabilityLimit
                    ? Math.max(0, Number(addon.available_quantity) - (addon.purchased_quantity || 0))
                    : Number.MAX_SAFE_INTEGER;
                  const isSoldOut = hasAvailabilityLimit && remainingQuantity === 0;
                  return(
                    <div key={i} className={isSoldOut ? "addon unavailable" : "addon"}>
                      <div className="addonDetails">
                        {hasAvailabilityLimit && <div className="addonAvailability">
                          {isSoldOut ? (
                            <span className="soldOut">Sold Out</span>
                          ) : (
                            <>
                              <span className="available">{remainingQuantity}</span> <span className="unavailable">/ {addon.available_quantity} items available</span>
                            </>
                          )}
                        </div>}
                        <div className="addonTitle">{addon.title}<span className="addonFee">{formatter.format(addon.fee)}</span></div>
                        <div className="addonDescription">{addon.description ? addon.description : "No Description Available"}</div>
                      </div>
                      <div className="addonActions">
                        <div className="decrementAddon">
                          <Button
                            disabled={addon.amount === 0}
                            onClick={() => {
                              setAddons(addons.map((a) => {
                                if (a._id === addon._id) {
                                  return { ...a, amount: a.amount ? a.amount - 1 : 0 };
                                }
                                return a;
                              }));
                            }}
                          >-</Button>
                        </div>
                        <Input
                          type="number"
                          className="addonQuantity"
                          min={0}
                          max={hasAvailabilityLimit ? remainingQuantity : undefined}
                          value={addon.amount || 0}
                          onChange={(e) => {
                            let quantity = parseInt(e.target.value);

                            if (e.target.value.length === 0) {
                              quantity = 0;
                            }

                            if (hasAvailabilityLimit) {
                              quantity = Math.min(quantity, remainingQuantity);
                            }

                            if (quantity >= 0 && (!hasAvailabilityLimit || quantity <= remainingQuantity)) {
                              setAddons(addons.map((a) => {
                                if (a._id === addon._id) {
                                  return { ...a, amount: quantity };
                                }
                                return a;
                              }));
                            }
                          }}
                          label="Quantity"
                        />
                        <div className="incrementAddon">
                          <Button
                            disabled={hasAvailabilityLimit ? addon.amount === remainingQuantity : false}
                            onClick={() => {
                              setAddons(addons.map((a) => {
                                if (a._id === addon._id) {
                                  return { ...a, amount: a.amount ? a.amount + 1 : 1 };
                                }
                                return a;
                              }));
                            }}
                          >+</Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {stripeOptions !== undefined && stripePromise !== undefined && (
              <Elements stripe={stripePromise} options={stripeOptions}>
                <Checkout
                  redirect={Config.web.REACT_APP_WEB_URL + "/e/" + eid + "?dids=" + getDogIds() + "&waitlist=" + checkIfWaitlist() }
                  userAddress={userAddress}
                  userPhone={userPhone}
                />
              </Elements>
            )}
          </div>}
        </div>
        <Cart
          onApplyDiscount={(discount: string) => setCurrentDiscountCode(discount) }
          shows={shows}
          paymentSummary={paymentSummary}
          isLottery={event?.is_lottery}
        />
      </div>
    </div>
  );
};

export default EventPayment;